<template>
  <ion-page>
    <ion-header class="ion-no-border events-page-header">
      <ion-toolbar>
        <ion-buttons slot="start">
          <ion-button @click="openMenu">
            <ion-icon :icon="menuOutline"></ion-icon>
          </ion-button>
        </ion-buttons>
        <ion-title>Настройки</ion-title>
        <ion-buttons slot="end">
          <HeaderMessengerButton />
        </ion-buttons>
      </ion-toolbar>
    </ion-header>
    <ion-content class="main-background settings-content" :fullscreen="true">
      <div class="settings-list">
        <div class="settings-card settings-card--theme">
          <div class="settings-theme-label">Тема</div>
          <ion-segment
            mode="ios"
            class="liga-glass-segment settings-theme-segment"
            :value="appearance"
            @ionChange="onAppearanceChange($event)"
          >
            <ion-segment-button mode="ios" value="system">
              <ion-label>Авто</ion-label>
            </ion-segment-button>
            <ion-segment-button mode="ios" value="dark">
              <ion-label>Тёмная</ion-label>
            </ion-segment-button>
            <ion-segment-button mode="ios" value="light">
              <ion-label>Светлая</ion-label>
            </ion-segment-button>
          </ion-segment>
          <p class="settings-theme-hint">Автоматически — по теме устройства</p>
        </div>
        <ion-item class="settings-card" lines="none">
          <ion-label>Меню справа</ion-label>
          <ion-toggle
            aria-label="Меню справа"
            @ionChange="menuButtonChange($event)"
            :checked="menuButton.position === 'right'"
          ></ion-toggle>
        </ion-item>
        <ion-item class="settings-card" lines="none">
          <ion-label>Кэш</ion-label>
          <ion-label slot="end" class="settings-meta" v-show="!removingCache">{{
            bufferSizeHuman
          }}</ion-label>
          <ion-icon
            :icon="trashOutline"
            class="settings-action-icon"
            v-show="bufferSize > 0 && !removingCache"
            @click="clearCacheExceptPlaylists('/')"
            slot="end"
          ></ion-icon>
          <ion-spinner name="bubbles" slot="end" v-show="removingCache"></ion-spinner>
        </ion-item>
        <ion-item class="settings-card" lines="none">
          <ion-label>Память устройства</ion-label>
          <ion-label slot="end" class="settings-meta">{{
            `${deviceDiskFreeHuman} из ${deviceDiskTotalHuman}`
          }}</ion-label>
        </ion-item>
        <ion-item
          v-if="isAuthenticated"
          class="settings-card settings-card--nav"
          button
          detail
          lines="none"
          @click="$router.push({ name: 'MessengerPrivacySettings' })"
        >
          <ion-label>
            <h2>Liga.Chat / Приватность</h2>
            <p>Видимость профиля и личные сообщения</p>
          </ion-label>
        </ion-item>

        <div class="settings-card settings-card--legal">
          <div class="settings-legal-links">
            <a :href="LEGAL.offerUrl" target="_blank" rel="noopener noreferrer" class="settings-legal-link">Оферта</a>
            <span class="settings-legal-sep">|</span>
            <button type="button" class="settings-legal-link" @click="$router.push({ name: 'PrivacyPolicy' })">
              Конфиденциальность
            </button>
            <span class="settings-legal-sep">|</span>
            <button type="button" class="settings-legal-link" @click="$router.push({ name: 'OfferSupplement' })">
              Возвраты
            </button>
          </div>
          <p class="settings-legal-payee">
            Платежи принимает:
            <a :href="LEGAL.offerUrl" target="_blank" rel="noopener noreferrer" class="settings-legal-link">
              {{ LEGAL.payeeShortName }}
            </a>
          </p>
        </div>
      </div>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { LEGAL } from "@/config/legal";
import { AppearancePreference, GlobalContext } from "@/model/globalcontext.model";
import { MenuButton } from "@/model/menubutton.model";
import {
  getCurrentAppearancePreference,
  getCurrentEffectiveTheme,
  markAppearancePickerSeen,
  setAppearancePreference,
} from "@/services/appearance.service";
import FilesystemCustomUtils from "@/services/filesystem.custom.utils";
import { Device } from "@capacitor/device";
import { Directory, Filesystem } from "@capacitor/filesystem";
import { Preferences } from "@capacitor/preferences";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonPage,
  IonSegment,
  IonSegmentButton,
  IonSpinner,
  IonTitle,
  IonToggle,
  IonToolbar,
  menuController,
} from "@ionic/vue";
import { menuOutline, trashOutline } from "ionicons/icons";
import { defineComponent, inject } from "vue";
import store from "@/store";

import HeaderMessengerButton from '@/components/HeaderMessengerButton.vue';

export default defineComponent({
  name: "Settings",
  components: {
    HeaderMessengerButton,
    IonButton,
    IonButtons,
    IonContent,
    IonHeader,
    IonIcon,
    IonItem,
    IonLabel,
    IonPage,
    IonSegment,
    IonSegmentButton,
    IonSpinner,
    IonTitle,
    IonToggle,
    IonToolbar,
  },
  setup() {
    const menuButton = inject("menuButton") as MenuButton;
    const filesystemCustomUtils = new FilesystemCustomUtils();
    const globalContext = inject("globalContext") as GlobalContext;
    const isMobile = globalContext.isMobile;

    Preferences.get({ key: "menuButtonRight" }).then((res) => {
      if (res.value) {
        menuButton.position = res.value;
      }
    });

    const openMenu = () => menuController.open("mainMenu");

    return {
      menuButton,
      trashOutline,
      menuOutline,
      openMenu,
      filesystemCustomUtils,
      isMobile,
      globalContext,
      LEGAL,
    };
  },

  data() {
    return {
      bufferSize: 0,
      removingCache: false,
      deviceDiskTotal: 0,
      deviceDiskFree: 0,
      appearance: getCurrentAppearancePreference() as AppearancePreference,
    };
  },

  computed: {
    isAuthenticated(): boolean {
      return !!store.getters.authenticated;
    },
    bufferSizeHuman(): string {
      return this.getBytesHuman(this.bufferSize);
    },
    deviceDiskTotalHuman(): string {
      return this.getBytesHuman(this.deviceDiskTotal);
    },
    deviceDiskFreeHuman(): string {
      return this.getBytesHuman(this.deviceDiskFree);
    },
  },

  ionViewWillEnter() {
    this.appearance = getCurrentAppearancePreference();
    this.bufferSize = 0;
    this.deviceDiskTotal = 0;
    this.deviceDiskFree = 0;
    this.countCache("/");

    if (this.isMobile) {
      Device.getInfo().then((deviceInfo) => {
        const nativeInfo = deviceInfo as any;
        this.deviceDiskTotal = nativeInfo.realDiskTotal || 0;
        this.deviceDiskFree = nativeInfo.realDiskFree || 0;
      });
    }
  },

  methods: {
    getBytesHuman(bytes: number): string {
      let bytesH = bytes + " B";
      if (bytes > 1000 && bytes < 1000000) {
        bytesH = Math.round(bytes / 1024) + " KB";
      } else if (bytes > 1000000 && bytes < 1000000000) {
        bytesH = Math.round(bytes / 1024 / 1024) + " MB";
      } else if (bytes > 1000000000) {
        bytesH = Math.round(bytes / 1024 / 1024 / 1024) + " GB";
      }
      return bytesH;
    },

    async onAppearanceChange(event: CustomEvent) {
      const value = (event.detail?.value || 'dark') as AppearancePreference;
      if (value !== 'system' && value !== 'dark' && value !== 'light') return;
      this.appearance = value;
      await setAppearancePreference(value);
      await markAppearancePickerSeen();
      this.globalContext.appearance = value;
      this.globalContext.effectiveTheme = getCurrentEffectiveTheme();
    },

    async menuButtonChange(event: any) {
      this.menuButton.position = event.detail.checked ? "right" : "left";

      await Preferences.set({
        key: "menuButtonRight",
        value: this.menuButton.position,
      });

      await menuController.enable(false, "mainMenu");
      await menuController.enable(true, "mainMenu");
    },

    async musicBetaChange(event: any) {
      await Preferences.set({
        key: "musicBeta",
        value: event.detail.checked ? "1" : "0",
      });

      this.globalContext.musicBeta = event.detail.checked;
    },

    async videoBetaChange(event: any) {
      await Preferences.set({
        key: "videoBeta",
        value: event.detail.checked ? "1" : "0",
      });

      this.globalContext.videoBeta = event.detail.checked;
    },

    async syncVideoGuideChange(event: any) {
      await Preferences.set({
        key: "syncVideoGuide",
        value: event.detail.checked ? "1" : "0",
      });
      this.globalContext.syncVideoGuide = event.detail.checked;
    },

    async countCache(path: string) {
      try {
        if (path && !path.endsWith("/")) {
          path = path + '/';
        }
        // Step 1: Read the directory
        const result = await Filesystem.readdir({
            path: path,
            directory: Directory.Data,
        });

        for (const file of result.files) {
            // Check if it's a directory
            const isDirectory = await this.checkIfDirectory(path + file.name);

            if (isDirectory) {
                // Recursively call the function for subdirectory
                await this.countCache(path + file.name);
            } else {
                // Exclude the playlists directory and playlist.json files
                if (file.name === "playlist.json") {
                    continue; // Skip this file
                }

                this.bufferSize += await this.getFileSize(path + file.name);
            }
        }
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        // Пустой/отсутствующий каталог Data на web — ожидаемо, не шумим в консоли.
        if (!/folder does not exist/i.test(message)) {
          console.error("Error counting cache:", error);
        }
      }
    },

    // Рекурсивная очистка кэша в Directory.Data. Удаляются все файлы, кроме playlist.json.
    // Включает: релизы (видео, музыка, гайды, картинки), офлайн-видео тренингов (Education_Offline) и прочие данные приложения.
    async clearCacheExceptPlaylists(path: string) {
      this.removingCache = true;

      try {
        if (path && !path.endsWith("/")) {
          path = path + '/';
        }
        const result = await Filesystem.readdir({
            path: path,
            directory: Directory.Data,
        });

        for (const file of result.files) {
            const isDirectory = await this.checkIfDirectory(path + file.name);

            if (isDirectory) {
                await this.clearCacheExceptPlaylists(path + file.name);
            } else {
                if (file.name === "playlist.json") {
                    continue;
                }

                // Удаляем файл (в т.ч. офлайн-видео тренингов, кэш релизов и т.д.)
                await Filesystem.deleteFile({
                    path: path + file.name,
                    directory: Directory.Data,
                });

                console.log(`Deleted: ${file.name}`);
            }
        }
      } catch (error) {
        console.error("Error clearing cache:", error);
      } finally {
        setTimeout(() => {
          this.removingCache = false;
          
          this.bufferSize = 0;
          this.deviceDiskTotal = 0;
          this.deviceDiskFree = 0;
          this.countCache("/");

          if (this.isMobile) {
            Device.getInfo().then((deviceInfo) => {
              const nativeInfo = deviceInfo as any;
              this.deviceDiskTotal = nativeInfo.realDiskTotal || 0;
              this.deviceDiskFree = nativeInfo.realDiskFree || 0;
            });
          }

        }, 2000);
      }
    },

    // Helper function to check if the path is a directory
    async checkIfDirectory(path: string) {
        try {
            const result = await Filesystem.stat({
                path: path,
                directory: Directory.Data,
            });
            return result.type === 'directory'; // Will return true if it's a directory
        } catch {
            return false; // If stat fails, it's not a directory
        }
    },

    async getFileSize(path: string) {
        try {
            const result = await Filesystem.stat({
                path: path,
                directory: Directory.Data,
            });
            
            if (result.type !== 'directory') {
              return result.size;
            }
        } catch {
            return 0;
        }

        return 0;
    }

  },
});
</script>

<style scoped>
.settings-content {
  --padding-top: 8px;
  --padding-bottom: 24px;
}

.settings-list {
  padding: 8px 14px 28px;
}

.settings-card {
  --background: var(--liga-surface, rgba(255, 255, 255, 0.07));
  --background-hover: var(--liga-tab-btn-hover, rgba(255, 255, 255, 0.11));
  --background-activated: var(--liga-tab-btn-hover, rgba(255, 255, 255, 0.09));
  --color: var(--liga-chrome-text, #fff);
  --border-color: transparent;
  --inner-border-width: 0;
  --border-radius: 16px;
  --min-height: 56px;
  --padding-start: 16px;
  --padding-end: 14px;
  --detail-icon-color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.55));
  --detail-icon-opacity: 1;
  margin: 0 0 10px;
  border: 1px solid var(--liga-surface-border, rgba(255, 255, 255, 0.14));
  border-radius: 16px;
  box-shadow: var(--liga-float-glass-shadow, 0 4px 14px rgba(0, 0, 0, 0.18));
  overflow: hidden;
  color: var(--liga-chrome-text, #fff);
}

.settings-card::part(native) {
  border-radius: 16px;
  background: transparent;
}

.settings-card--theme {
  padding: 14px 14px 12px;
  background: var(--liga-surface, rgba(255, 255, 255, 0.07));
}

.settings-theme-label {
  font-weight: 650;
  font-size: 0.98rem;
  margin-bottom: 10px;
  color: var(--liga-chrome-text, #fff);
}

.settings-theme-segment {
  margin: 0;
  width: 100%;
  max-width: 100%;
}

.settings-theme-hint {
  margin: 8px 2px 0;
  font-size: 0.8rem;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.55));
}

.settings-card ion-label {
  color: var(--liga-chrome-text, #fff);
  font-weight: 600;
  font-size: 0.98rem;
}

.settings-card .settings-meta {
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.7));
  font-weight: 500;
  font-size: 0.9rem;
  margin-inline-start: 8px;
}

.settings-card--nav {
  --min-height: 72px;
}

.settings-card h2 {
  margin: 0 0 4px;
  color: var(--liga-chrome-text, #fff);
  font-size: 1rem;
  font-weight: 650;
  line-height: 1.25;
}

.settings-card p {
  margin: 0;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.62));
  font-size: 0.85rem;
  font-weight: 400;
  line-height: 1.3;
}

.settings-action-icon {
  color: var(--liga-chrome-text, rgba(255, 255, 255, 0.9));
  font-size: 1.25rem;
  margin-inline-start: 8px;
  cursor: pointer;
}

ion-toggle {
  --background-checked: var(--ion-color-orange);
  --background: rgba(255, 255, 255, 0.18);
  --handle-background: #fff;
  --handle-background-checked: #fff;
}

ion-spinner {
  --color: var(--ion-color-orange);
  margin-inline-start: 8px;
}

.settings-card--legal {
  padding: 14px 14px 12px;
  background: var(--liga-surface, rgba(255, 255, 255, 0.07));
  text-align: center;
}

.settings-legal-links {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 0.3em;
  margin-bottom: 8px;
}

.settings-legal-link {
  appearance: none;
  border: none;
  background: transparent;
  padding: 0;
  margin: 0;
  color: var(--ion-color-orange-tint, #eb5952);
  font-size: 0.88rem;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
}

.settings-legal-link:hover {
  text-decoration: underline;
}

.settings-legal-sep {
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.45));
  user-select: none;
}

.settings-legal-payee {
  margin: 0;
  font-size: 0.8rem;
  color: var(--liga-chrome-text-muted, rgba(255, 255, 255, 0.7));
  line-height: 1.35;
}
</style>
